import React, { useMemo } from 'react';
import { useHistory } from 'react-router-dom';

// Components
import * as S from './Styled';
import Icon from '../../Icon';
import LangSwitcher from '../../LangSwitcher';
import Selecter from '../../Selecter';

// Store
import { selectLocation, TheBehaviorDrawer } from '../../../actions';
import { useDispatch, useSelector } from 'react-redux';

// Utils
import { useTranslation } from 'react-i18next';
import toast from '../../../utils/toast';
import localStorage from 'store';
import { getValueStorage } from '../../../utils';

// Config
import { side_nav } from '../../../config/side_nav';
import Location from './Location';
import { MenuOutlined } from '@ant-design/icons';
import CatMenuTriggerButton from '../../CategoryMenu/CatMenuTriggerButton';
import { useAccessibility } from '../../AccessibilityToolbar';
import EyeIcon from '../../AccessibilityToolbar/EyeIcon';

const SideNav = ({ onLoginModal, onOpenCart, onToggleMenu, showMenu }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { sideNav } = useSelector(state => state.ui);
  const { location, isLoading } = useSelector(state => state.user);
  const { locationConfirmed } = useSelector(state => state.user);
  const { isAuthenticated, user } = useSelector(state => state.auth);
  const favoritesSize = useSelector(state => state.user.favorites.length);
  const cartSize = useSelector(state => state.cart.items.length);

  const insurances = useMemo(() => {
    if (isAuthenticated) return user?.insurances;
  }, [user, isAuthenticated]);

  const insuranceItem = getValueStorage('insurance_item');

  const handleInsurance = item => {
    if (insurances?.length > 0) {
      localStorage.set(
        'insurance_item',
        insurances.find(({ title }) => title === item)
      );
      window.location.reload();
    }
  };

  const history = useHistory();
  const onCloseDrawer = () => dispatch(TheBehaviorDrawer(false));
  const { toggleToolbar } = useAccessibility();

  const onCart = () => {
    if (!cartSize) {
      return toast.info({
        message: t('messages.info.cart_is_empty'),
        placement: 'bottomRight',
      });
    }

    onOpenCart();
  };

  const onChangeCity = event => {
    event.preventDefault();
    event.stopPropagation();
    dispatch(TheBehaviorDrawer(false));
  };

  const onSelectCity = async item => {
    let location = {
      ...item,
      name: item?.address?.split(',')?.[0] ?? item?.name,
    };
    await dispatch(selectLocation(location));
    dispatch(TheBehaviorDrawer(false));
  };

  const catProps = {
    onToggleMenu: show => {
      onToggleMenu(show);
      onCloseDrawer();
    },
    showMenu,
    style: {
      marginTop: 10,
      marginLeft: 10,
    },
    icon: <MenuOutlined style={{ marginRight: 8, fontSize: 15 }} />,
  };

  return (
    <>
      <S.SideNav visible={sideNav}>
        <S.CloseBtn onClick={onCloseDrawer}>
          <Icon name="close" />
        </S.CloseBtn>
        <S.Content>
          <S.MainWrap>
            <S.Section>
              <S.BtnContainer>
                <CatMenuTriggerButton {...catProps} type="block">
                  {t('labels.catalog_products')}
                </CatMenuTriggerButton>
              </S.BtnContainer>
              {insurances?.length > 0 && (
                insurances.length === 1 ? (
                  <S.InsuranceSingleWrap>
                    <S.InsuranceLabel>{insurances[0].title}</S.InsuranceLabel>
                  </S.InsuranceSingleWrap>
                ) : (
                  <S.InsuranceWrap>
                    <Selecter
                      items={insurances}
                      onSelect={item => handleInsurance(item)}
                      fontSize="13px"
                      activeItem={insuranceItem}
                      withSearch
                      searchPlaceholder="Пошук страхової..."
                    />
                  </S.InsuranceWrap>
                )
              )}
            </S.Section>
            <S.Section>
              <Location
                location={location}
                isLoading={isLoading}
                locationConfirmed={locationConfirmed}
                onChangeCity={onChangeCity}
                onSelectCity={onSelectCity}
              />
            </S.Section>
            <S.Section>
              {!isAuthenticated ? (
                <S.StyledLink
                  to=""
                  onClick={() => {
                    onCloseDrawer();
                    onLoginModal();
                  }}
                  className="auth-link"
                >
                  <S.IconWrap>
                    <Icon name="user" size="small" />
                  </S.IconWrap>
                  {t('buttons.log_in_to_your_personal_account')}
                </S.StyledLink>
              ) : null}
              <S.StyledLink
                to=""
                onClick={() => {
                  onCloseDrawer();
                  onCart();
                }}
              >
                <S.IconWrap>
                  <Icon name="cart" size="small" />
                  {cartSize ? <S.Count>{cartSize}</S.Count> : null}
                </S.IconWrap>
                <S.Label>{t('labels.shopping_cart')}</S.Label>
              </S.StyledLink>
              <S.StyledLink to="/favorites" onClick={onCloseDrawer}>
                <S.IconWrap>
                  <Icon name="heart" size="small" />
                  {favoritesSize ? <S.Count>{favoritesSize}</S.Count> : null}
                </S.IconWrap>
                <S.Label>{t('labels.favorites')}</S.Label>
              </S.StyledLink>
              <S.StyledLink
                to=""
                onClick={e => {
                  e.preventDefault();
                  onCloseDrawer();
                  toggleToolbar();
                }}
              >
                <S.IconWrap>
                  <EyeIcon />
                </S.IconWrap>
                <S.Label>{t('accessibilityToolbar.toggle')}</S.Label>
              </S.StyledLink>
            </S.Section>
            <S.Section>
              <S.Menu>
                {side_nav?.map(({ id, title, to }) => (
                  <li key={id}>
                    <S.StyledLink
                      to={to}
                      onClick={e => {
                        e.preventDefault();
                        onCloseDrawer();
                        history.push(to);
                      }}
                    >
                      {t(`labels.${title}`)}
                    </S.StyledLink>
                  </li>
                ))}
              </S.Menu>
            </S.Section>
          </S.MainWrap>
          <S.SecondaryNav>
            <LangSwitcher />
          </S.SecondaryNav>
        </S.Content>
      </S.SideNav>
      <S.Overlay visible={sideNav} onClick={onCloseDrawer} />
    </>
  );
};

export default SideNav;
